import React, {
  useMemo,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import UserService from '../../services/UserService';
import SuperAdminService from '../../services/SuperAdminService';
import { USER_ROLES, ORG_ROLES } from '../../constants/main';
import RoundedInput from '../../components/Inputs/RoundedInput';
import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import { UiContext } from '../../context/UiContext';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import { saveAuthData } from '../../helpers/authStorage';
import { useUiStore } from '../../store';
import DotsLoader from '../../components/DotsLoader';
import usersIcon from '../../assets/images/my-meetings/users.svg';
import infoIcon from '../../assets/images/my-meetings/info.svg';
import downArrow from '../../assets/images/down-arrow.svg';

import classes from './styles.module.scss';

const emptyOwnerForm = {
  name: '',
  email: '',
  title: '',
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) {
    return '0 MB';
  }

  const megabytes = bytes / (1024 * 1024);
  const rounded = megabytes >= 10 ? megabytes.toFixed(1) : megabytes.toFixed(2);
  return `${rounded} MB`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return value;
  }
};

const formatAmount = (amount, currency = 'usd') => {
  if (amount === null || amount === undefined) return '—';
  return `$${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
};

export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [ownerFormValues, setOwnerFormValues] = useState(emptyOwnerForm);
  const [ownerFormErrors, setOwnerFormErrors] = useState({});
  const [isStartingImpersonation, setIsStartingImpersonation] = useState(false);
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  const [isDestroyModalVisible, setIsDestroyModalVisible] = useState(false);
  const [destroyOrgName, setDestroyOrgName] = useState('');
  const [isDestroyUserModalVisible, setIsDestroyUserModalVisible] =
    useState(false);
  const [destroyUserTarget, setDestroyUserTarget] = useState(null);
  const [destroyUserEmail, setDestroyUserEmail] = useState('');
  const [usersSortField, setUsersSortField] = useState('name');
  const [usersSortOrder, setUsersSortOrder] = useState('ASC');

  const { data: currentUser, isLoading: isLoadingUser } = useQuery(
    'me',
    UserService.getMe
  );

  const { data: orgRoles } = useQuery(
    'super-admin-org-roles',
    SuperAdminService.getOrgRoles,
    {
      enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN,
      staleTime: 5 * 60 * 1000,
    }
  );

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    error: organizationError,
  } = useQuery(
    ['super-admin-organization', id],
    () => SuperAdminService.getOrganization(id),
    {
      enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN && !!id,
      retry: false,
    }
  );

  const organization = organizationData?.data || organizationData || null;

  const {
    data: organizationUsersResponse,
    isLoading: isLoadingOrganizationUsers,
    refetch: refetchOrganizationUsers,
  } = useQuery(
    ['super-admin-organization-users', id],
    () =>
      SuperAdminService.getUsers({
        organizationId: id,
        perPage: 100,
      }),
    {
      enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN && !!id,
    }
  );

  const organizationUsers = useMemo(
    () => organizationUsersResponse?.data || [],
    [organizationUsersResponse]
  );

  const usersTableHeaders = useMemo(
    () => [
      { field: 'name', title: 'Name' },
      { field: 'email', title: 'Email' },
      { field: 'role', title: 'Role' },
      { field: 'action', title: 'Action', noSort: true },
    ],
    []
  );

  const sortedOrganizationUsers = useMemo(() => {
    const valueForField = (user, field) => {
      switch (field) {
        case 'name':
          return user?.name || '';
        case 'email':
          return user?.email || '';
        case 'role':
          return user?.orgRole?.access || user?.role || '';
        default:
          return '';
      }
    };

    const sorted = [...organizationUsers].sort((a, b) => {
      const aValue = valueForField(a, usersSortField);
      const bValue = valueForField(b, usersSortField);

      if (usersSortOrder === 'ASC') {
        return String(aValue).localeCompare(String(bValue));
      }
      return String(bValue).localeCompare(String(aValue));
    });

    return sorted;
  }, [organizationUsers, usersSortField, usersSortOrder]);

  const ownerRoleId = useMemo(
    () => orgRoles?.find((role) => role.access === ORG_ROLES.OWNER)?.id || null,
    [orgRoles]
  );

  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;
  const impersonation = currentUser?.impersonation;

  const {
    data: storageUsage,
    isLoading: isLoadingStorage,
  } = useQuery(
    ['super-admin-organization-storage', id],
    () => SuperAdminService.getOrganizationStorage(id),
    {
      enabled: isSuperAdmin && !!id,
      staleTime: 60 * 1000,
    }
  );

  const totalStorageBytes = storageUsage?.totals?.totalBytes || 0;
  const trashStorageBytes = storageUsage?.trashTotals?.totalBytes || 0;
  const totalCombinedStorageBytes = totalStorageBytes + trashStorageBytes;
  const formattedTrashStorage = useMemo(
    () => formatBytes(trashStorageBytes),
    [trashStorageBytes]
  );

  // Format storage with effective quota: storage_quota override, else tier limits, else Free default
  const formattedStorageWithQuota = useMemo(() => {
    if (isLoadingStorage) {
      return '—';
    }
    const storageQuotaMB = organization?.metadata?.storage_quota;
    const tierSnapshot = organization?.metadata?.tierSnapshot || {};
    const tierLimitsMb =
      organization?.subscription?.tier?.storageLimitMb ||
      tierSnapshot.storageLimitMb ||
      null;
    const effectiveLimitMb =
      storageQuotaMB === 0 || storageQuotaMB
        ? Number(storageQuotaMB)
        : tierLimitsMb || 1024; // fallback to Free default
    const usedMB = totalCombinedStorageBytes / (1024 * 1024);
    const formattedUsed = usedMB >= 10 ? usedMB.toFixed(1) : usedMB.toFixed(2);
    
    return `${formattedUsed} MB / ${effectiveLimitMb} MB`;
  }, [
    isLoadingStorage,
    organization?.metadata?.storage_quota,
    organization?.metadata?.tierSnapshot,
    organization?.subscription?.tier,
    totalCombinedStorageBytes,
  ]);

  const planInfo = useMemo(() => {
    const tierSnapshot = organization?.metadata?.tierSnapshot || {};
    const tier = organization?.subscription?.tier || tierSnapshot || {};
    return {
      name: tier.name || 'Free',
      status: organization?.subscription?.status || tierSnapshot.status || 'free',
    };
  }, [organization]);

  const summaryMetrics = useMemo(
    () => [
      {
        label: 'Total Users',
        value: isLoadingOrganizationUsers ? '—' : organizationUsers.length,
        description: 'Total number of users in this organization',
      },
      {
        label: 'Total Storage Used',
        value: formattedStorageWithQuota,
        description: isLoadingStorage
          ? 'Loading storage information...'
          : `≈ ${(totalCombinedStorageBytes / (1024 * 1024 * 1024)).toFixed(2)} GB combined storage (used + trash)`,
        spanTwoColumns: true,
      },
      {
        label: 'Trash Storage',
        value: isLoadingStorage ? '—' : formattedTrashStorage,
        description: isLoadingStorage
          ? 'Loading storage information...'
          : `≈ ${(trashStorageBytes / (1024 * 1024 * 1024)).toFixed(2)} GB in trash`,
      },
    ],
    [
    isLoadingOrganizationUsers,
    organizationUsers.length,
    isLoadingStorage,
      formattedStorageWithQuota,
    formattedTrashStorage,
    trashStorageBytes,
    totalCombinedStorageBytes,
    ]
  );

  const subscriptionDetails = useMemo(() => {
    const subscription = organization?.subscription;
    const tierSnapshot = organization?.metadata?.tierSnapshot || {};
    const tier = subscription?.tier || tierSnapshot || {};
    const seatQuotaOverride = organization?.metadata?.seat_quota;
    const seatQuotaValue = Number(seatQuotaOverride);
    const effectiveSeatLimit =
      Number.isFinite(seatQuotaValue) && seatQuotaValue >= 1
        ? seatQuotaValue
        : tier.seatLimit;

    const planDetails = [
      { label: 'Plan', value: tier.name || 'Free' },
      { label: 'Status', value: subscription?.status || tierSnapshot.status || 'free' },
      { label: 'Amount', value: formatAmount(tier.amount, tier.currency || 'usd') },
      {
        label: 'Seats',
        value: effectiveSeatLimit ? `${effectiveSeatLimit} users` : '—',
      },
      {
        label: 'Storage',
        value: tier.storageLimitMb ? `${(tier.storageLimitMb / 1024).toFixed(0)} GB` : '—',
      },
    ];

    const stripeDetails = [
      { label: 'Stripe Price ID', value: tier.priceId || subscription?.stripePriceId || '—' },
      { label: 'Stripe Subscription ID', value: subscription?.stripeSubscriptionId || '—' },
      { label: 'Stripe Customer ID', value: subscription?.stripeCustomerId || '—' },
      { label: 'Current Period Start', value: formatDateTime(subscription?.currentPeriodStart) },
      { label: 'Current Period End', value: formatDateTime(subscription?.currentPeriodEnd) },
      { label: 'Cancel At', value: formatDateTime(subscription?.cancelAt) },
      {
        label: 'Cancel At Period End',
        value: subscription?.cancelAtPeriodEnd === true ? 'Yes' : 'No',
      },
    ];

    return { planDetails, stripeDetails };
  }, [organization]);

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  useEffect(() => {
    if (organization) {
      setBreadcrumbsOptions([
        { label: 'Super Admin', to: '/super-admin' },
        { label: organization.name, to: '#' },
      ]);
    } else {
      setBreadcrumbsOptions([
        { label: 'Super Admin', to: '/super-admin' },
        { label: 'Organization', to: '#' },
      ]);
    }
  }, [setBreadcrumbsOptions, organization]);

  const handleOwnerFormChange = (field, value) => {
    setOwnerFormValues((prev) => ({ ...prev, [field]: value }));
    if (ownerFormErrors[field]) {
      setOwnerFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateOwnerForm = useCallback((values) => {
    const errors = {};
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!values.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = 'Invalid email format';
    }
    return errors;
  }, []);

  const { mutateAsync: createOrgAdmin, isLoading: ownerFormIsBusy } =
    useMutation((payload) => SuperAdminService.createUser(payload));

  const handleStartOwnerForm = () => {
    setShowOwnerForm(true);
    setOwnerFormValues(emptyOwnerForm);
    setOwnerFormErrors({});
  };

  const handleCancelOwnerForm = () => {
    setShowOwnerForm(false);
    setOwnerFormValues(emptyOwnerForm);
    setOwnerFormErrors({});
  };

  const submitOwnerForm = async () => {
    if (!id || !ownerRoleId) {
      notifyError('Organization role data not ready yet');
      return;
    }

    const errors = validateOwnerForm(ownerFormValues);
    if (Object.keys(errors).length > 0) {
      setOwnerFormErrors(errors);
      return;
    }

    try {
      await createOrgAdmin({
        name: ownerFormValues.name.trim(),
        email: ownerFormValues.email.trim().toLowerCase(),
        title: ownerFormValues.title.trim() || undefined,
        organizationId: id,
        orgRoleId: ownerRoleId,
      });
      notifySuccess('Organization admin created');
      setOwnerFormValues(emptyOwnerForm);
      setOwnerFormErrors({});
      setShowOwnerForm(false);
      await refetchOrganizationUsers();
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to create organization admin'
      );
      if (error?.response?.data?.errors) {
        setOwnerFormErrors(error.response.data.errors);
      }
    }
  };

  const handleStartImpersonation = useCallback(async () => {
    if (!id || isStartingImpersonation) {
      return;
    }

    setIsStartingImpersonation(true);
    try {
      const response = await SuperAdminService.startImpersonation(
        id,
        ORG_ROLES.OWNER
      );

      saveAuthData({
        userId: response.userId,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        name: response.name,
      });
      queryClient.invalidateQueries('me');
      notifySuccess('Impersonation started');
      setIsStartingImpersonation(false);
      window.location.href = '/my-videos';
    } catch (error) {
      setIsStartingImpersonation(false);
      notifyError(
        error?.response?.data?.message || 'Failed to start impersonation'
      );
    }
  }, [id, isStartingImpersonation, queryClient, notifySuccess, notifyError]);

  const handleStopImpersonation = useCallback(async () => {
    if (isStoppingImpersonation) {
      return;
    }

    setIsStoppingImpersonation(true);
    try {
      const response = await SuperAdminService.endImpersonation();

      saveAuthData({
        userId: response.userId,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        name: response.name,
      });
      queryClient.invalidateQueries('me');
      notifySuccess('Impersonation stopped');
      setIsStoppingImpersonation(false);
      window.location.href = '/super-admin';
    } catch (error) {
      setIsStoppingImpersonation(false);
      notifyError(
        error?.response?.data?.message || 'Failed to stop impersonation'
      );
    }
  }, [isStoppingImpersonation, queryClient, notifySuccess, notifyError]);

  const { mutateAsync: destroyOrganization, isLoading: isDestroyingOrganization } =
    useMutation((payload) => SuperAdminService.deleteOrganization(id, payload));

  const { mutateAsync: destroyUser, isLoading: isDestroyingUser } = useMutation(
    ({ userId, payload }) => SuperAdminService.destroyUser(userId, payload)
  );

  const openDestroyModal = () => {
    setDestroyOrgName('');
    setIsDestroyModalVisible(true);
  };

  const closeDestroyModal = () => {
    setIsDestroyModalVisible(false);
    setDestroyOrgName('');
  };

  const confirmDestroyOrganization = async () => {
    if (!organization?.name || !id) {
      notifyError('Organization not loaded');
      return;
    }

    try {
      await destroyOrganization({ confirmOrgName: destroyOrgName.trim() });
      notifySuccess('Organization destroyed');
      queryClient.removeQueries(['super-admin-organization', id]);
      queryClient.removeQueries(['super-admin-organization-users', id]);
      navigate('/super-admin');
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to destroy organization'
      );
    }
  };

  const openDestroyUserModal = (targetUser) => {
    setDestroyUserTarget(targetUser);
    setDestroyUserEmail('');
    setIsDestroyUserModalVisible(true);
  };

  const closeDestroyUserModal = () => {
    setIsDestroyUserModalVisible(false);
    setDestroyUserTarget(null);
    setDestroyUserEmail('');
  };

  const confirmDestroyUser = async () => {
    const target = destroyUserTarget;
    if (!target?.id || !target?.email) {
      notifyError('User not loaded');
      return;
    }

    try {
      await destroyUser({
        userId: target.id,
        payload: { confirmEmail: destroyUserEmail.trim() },
      });
      notifySuccess('User destroyed');
      closeDestroyUserModal();
      await refetchOrganizationUsers();
    } catch (error) {
      notifyError(
        error?.response?.data?.message || error?.message || 'Failed to destroy user'
      );
    }
  };

  const renderUsersContent = () => {
    if (isLoadingOrganizationUsers) {
      return (
        <p className={classes.ownerPlaceholder}>Loading users…</p>
      );
    }

    if (organizationUsers.length === 0) {
      return (
        <p className={classes.ownerPlaceholder}>
          No users found for this organization.
        </p>
      );
    }

    return (
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              {usersTableHeaders.map((th) => {
                const isSorted = usersSortField === th.field && !th.noSort;
                const isAsc = usersSortOrder === 'ASC';

                return (
                  <th
                    key={th.field}
                    className={th.noSort ? undefined : classes.sortableHeader}
                    onClick={() => {
                      if (th.noSort) return;
                      setUsersSortField(th.field);
                      setUsersSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
                    }}
                  >
                    {th.title}{' '}
                    {!th.noSort && (
                      <span
                        className={classes.sortIcon}
                        style={{ visibility: isSorted ? 'visible' : 'hidden' }}
                      >
                        <img
                          src={downArrow}
                          alt="Sort order"
                          className={isSorted && isAsc ? classes.sortAsc : undefined}
                        />
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedOrganizationUsers.map((orgUser) => {
              const isOwner = orgUser.orgRole?.access === ORG_ROLES.OWNER;
              const isViewer = orgUser.orgRole?.access === ORG_ROLES.VIEWER;
              let roleLabel = orgUser.role || '—';
              if (orgUser.orgRole?.access) {
                roleLabel = orgUser.orgRole.access;
              }
              if (isOwner) {
                roleLabel = 'Admin';
              }

              let roleNode = roleLabel;
              if (isOwner) {
                roleNode = (
                  <span className={classes.statusBadge} data-status="owner">
                    Admin
                  </span>
                );
              } else if (isViewer) {
                roleNode = (
                  <span className={classes.statusBadge} data-status="viewer">
                    Viewer
                  </span>
                );
              }

              const isProtectedTarget =
                orgUser.role === USER_ROLES.SUPER_ADMIN ||
                orgUser.id === currentUser?.id;

              const destroyDisabled =
                impersonation?.isImpersonating ||
                isDestroyingUser ||
                isProtectedTarget;

              return (
                <tr key={orgUser.id}>
                  <td data-label="Name">{orgUser.name || '—'}</td>
                  <td data-label="Email">{orgUser.email}</td>
                  <td data-label="Role">{roleNode}</td>
                  <td data-label="Action">
                    <FilledButton
                      type="button"
                      color="#C62828"
                      isDisabled={destroyDisabled}
                      onClick={() => openDestroyUserModal(orgUser)}
                      data-testid={`destroy-user-${orgUser.id}`}
                    >
                      Destroy
                    </FilledButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoadingUser || isLoadingOrganization) {
    return (
      <Layout hasUserBlock>
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      </Layout>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/my-videos" replace />;
  }

  if (organizationError || !organization) {
    return (
      <Layout hasUserBlock>
        <div className={classes.emptyState}>
          {organizationError?.response?.status === 404
            ? 'Organization not found'
            : 'Failed to load organization'}
        </div>
      </Layout>
    );
  }

  return (
    <Layout hasUserBlock>
      <div className={classes.OrganizationDetailPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[
                { label: 'Super Admin', to: '/super-admin' },
                { label: organization?.name || 'Organization', to: '#' },
              ]}
            />
          </div>

          <section className={classes.headerCard}>
            <div className={classes.headerTop}>
              <div className={classes.headerInfo}>
                <div className={classes.titleRow}>
                <h1>{organization?.name || 'Organization'}</h1>
                  <span className={classes.planBadge}>
                    {planInfo.name} · {planInfo.status}
                  </span>
                </div>
                <p className={classes.subtitle}>
                  Organization details and management
                </p>
              </div>
              <div className={classes.headerActions}>
                <FilledButton
                  type="button"
                  onClick={handleStartImpersonation}
                  disabled={isStartingImpersonation}
                >
                  {isStartingImpersonation
                    ? 'Switching...'
                    : 'Act as Organization Admin'}
                </FilledButton>
                <FilledButton
                  onClick={() => navigate(`/super-admin/organizations/${id}/edit`)}
                >
                  Edit Organization
                </FilledButton>
                <FilledButton
                  type="button"
                  color="#C62828"
                  onClick={openDestroyModal}
                  disabled={
                    impersonation?.isImpersonating || isDestroyingOrganization
                  }
                  data-testid="destroy-organization-button"
                >
                  Destroy Organization
                </FilledButton>
                {impersonation?.isImpersonating && (
                  <OutlinedButton
                    onClick={handleStopImpersonation}
                    disabled={isStoppingImpersonation}
                  >
                    {isStoppingImpersonation
                      ? 'Ending…'
                      : 'Return to Super Admin'}
                  </OutlinedButton>
                )}
              </div>
            </div>

            <div className={classes.metricsGrid}>
              {summaryMetrics.map((metric) => (
                <div
                  key={metric.label}
                  className={classes.metricCard}
                  style={metric.spanTwoColumns ? { gridColumn: 'span 2' } : undefined}
                >
                  <span className={classes.metricLabel}>{metric.label}</span>
                  <span className={classes.metricValue}>{metric.value}</span>
                  {metric.description && (
                    <p className={classes.metricDescription}>
                      {metric.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className={classes.sectionCard}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionTitle}>
                <span className={classes.sectionIcon}>
                  <img src={infoIcon} alt="" />
                </span>
                <h3>Organization Details</h3>
              </div>
            </div>

            <div className={classes.infoGrid}>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Organization Name</span>
                <span className={classes.infoValue}>{organization?.name || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Organization ID</span>
                <span className={classes.infoValue}>{organization?.id || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Billing Address</span>
                <span className={classes.infoValue}>{organization?.billingAddress || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>City</span>
                <span className={classes.infoValue}>{organization?.city || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>State</span>
                <span className={classes.infoValue}>{organization?.state || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>ZIP Code</span>
                <span className={classes.infoValue}>{organization?.zip || '—'}</span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Created At</span>
                <span className={classes.infoValue}>
                  {organization?.createdAt
                    ? new Date(organization.createdAt).toLocaleDateString()
                    : '—'}
                </span>
              </div>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Updated At</span>
                <span className={classes.infoValue}>
                  {organization?.updatedAt
                    ? new Date(organization.updatedAt).toLocaleDateString()
                    : '—'}
                </span>
              </div>
            </div>
          </section>

          <ConfirmActionModal
            title="Destroy organization?"
            show={isDestroyModalVisible}
            handleClose={closeDestroyModal}
            onConfirm={confirmDestroyOrganization}
            confirmDisabled={
              isDestroyingOrganization ||
              destroyOrgName.trim() !== (organization?.name || '').trim()
            }
          >
            <div>
              <p>
                type <strong>{organization?.name}</strong> to delete organization,
                all of its users and all the content permanently
              </p>
              <ul className={classes.dangerList}>
                <li>All users in this organization will be deleted.</li>
                <li>All content for this organization will be deleted permanently.</li>
              </ul>
              <RoundedInput
                label="Organization name"
                value={destroyOrgName}
                onChange={(event) => setDestroyOrgName(event.target.value)}
              />
            </div>
          </ConfirmActionModal>

          <section className={classes.sectionCard}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionTitle}>
                <span className={classes.sectionIcon}>
                  <img src={infoIcon} alt="" />
                </span>
                <h3>Subscription Details</h3>
              </div>
            </div>

            <div className={classes.infoGrid}>
              {subscriptionDetails.planDetails.map((item) => (
                <div key={item.label} className={classes.infoItem}>
                  <span className={classes.infoLabel}>{item.label}</span>
                  <span className={classes.infoValue}>{item.value || '—'}</span>
                </div>
              ))}
            </div>

            <div className={classes.infoList}>
              {subscriptionDetails.stripeDetails.map((item) => (
                <div key={item.label} className={classes.infoRow}>
                  <span className={classes.infoLabel}>{item.label}</span>
                  <span className={classes.infoValue}>{item.value || '—'}</span>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.sectionCard}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionTitle}>
                <span className={classes.sectionIcon}>
                  <img src={usersIcon} alt="" />
                </span>
                <h3>Users</h3>
              </div>
              <FilledButton
                onClick={handleStartOwnerForm}
                disabled={!ownerRoleId || showOwnerForm}
              >
                Add Org Admin
              </FilledButton>
            </div>
            {showOwnerForm && (
              <div className={classes.ownerForm}>
                <h5>Create organization admin</h5>
                <p className={classes.ownerHint}>
                  We&apos;ll email them an invitation with a link to set their password.
                </p>
                <div className={classes.formGrid}>
                  <RoundedInput
                    label={ownerFormErrors.name || 'Full name'}
                    value={ownerFormValues.name}
                    errors={ownerFormErrors.name}
                    isTouched={!!ownerFormErrors.name}
                    onChange={(event) =>
                      handleOwnerFormChange('name', event.target.value)
                    }
                  />
                  <RoundedInput
                    label={ownerFormErrors.email || 'Email'}
                    value={ownerFormValues.email}
                    errors={ownerFormErrors.email}
                    isTouched={!!ownerFormErrors.email}
                    onChange={(event) =>
                      handleOwnerFormChange('email', event.target.value)
                    }
                  />
                  <RoundedInput
                    label={ownerFormErrors.title || 'Title (optional)'}
                    value={ownerFormValues.title}
                    errors={ownerFormErrors.title}
                    isTouched={!!ownerFormErrors.title}
                    onChange={(event) =>
                      handleOwnerFormChange('title', event.target.value)
                    }
                  />
                </div>
                <div className={classes.actions}>
                  <OutlinedButton
                    onClick={handleCancelOwnerForm}
                    disabled={ownerFormIsBusy}
                    style={{ minWidth: '280px' }}
                  >
                    Cancel
                  </OutlinedButton>
                  <FilledButton
                    type="button"
                    onClick={submitOwnerForm}
                    disabled={ownerFormIsBusy}
                    style={{ minWidth: '280px' }}
                  >
                    {ownerFormIsBusy ? 'Saving…' : 'Create org admin'}
                  </FilledButton>
                </div>
              </div>
            )}
            {renderUsersContent()}
          </section>

          <ConfirmActionModal
            title="Destroy user?"
            show={isDestroyUserModalVisible}
            handleClose={closeDestroyUserModal}
            onConfirm={confirmDestroyUser}
            confirmDisabled={
              isDestroyingUser ||
              destroyUserEmail.trim().toLowerCase() !==
                String(destroyUserTarget?.email || '').trim().toLowerCase()
            }
          >
            <div>
              <p>
                type <strong>{destroyUserTarget?.email}</strong> to delete this user
                permanently
              </p>
              <ul className={classes.dangerList}>
                <li>
                  Any shared resource created by this user will be unstable.
                </li>
                <li>
                  All folders and videos created by this user will also be deleted.
                </li>
                <li>
                  Any content inside folders created by this user will be deleted too (even if created by other users).
                </li>
              </ul>
              <RoundedInput
                label="User email"
                value={destroyUserEmail}
                onChange={(event) => setDestroyUserEmail(event.target.value)}
              />
            </div>
          </ConfirmActionModal>
        </div>
      </div>
    </Layout>
  );
}
